/** SUNTRONIX ONE — Company Profile settings (Commercial quotations)
 *  ----------------------------------------------------------------
 *  Admin-only (super_admin / management) screen for editing every
 *  field that appears on the quotation PDF — name, address, GSTIN,
 *  MSME, banking, signatory, sales-contact — and uploading the
 *  authorised-signature image that gets stamped on issued quotes.
 *  Nothing here invents legal / commercial data.  Whatever the admin
 *  saves is what the PDF prints.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Settings, Save, RefreshCw, Upload, Trash2, ArrowLeft, Building2,
  ShieldCheck, Banknote,
} from "lucide-react";
import { GovernedBanner, GovernedTag, useGovernedFields } from "@/components/kyc/GovernedField";


const FIELDS = [
  { section: "Brand",         key: "name",              label: "Company Name" },
  { section: "Brand",         key: "tagline",          label: "Tagline" },
  { section: "Brand",         key: "sales_contact",  label: "Sales Team Contact (phone / email)" },

  { section: "Address",       key: "address_line_1",  label: "Address Line 1" },
  { section: "Address",       key: "address_line_2",  label: "Address Line 2" },
  { section: "Address",       key: "city",                  label: "City" },
  { section: "Address",       key: "state",                label: "State" },
  { section: "Address",       key: "postal_code",      label: "Postal Code" },
  { section: "Address",       key: "country",             label: "Country" },
  { section: "Address",       key: "phone",                label: "Phone" },
  { section: "Address",       key: "email",                 label: "Email" },
  { section: "Address",       key: "website",              label: "Website" },

  { section: "Legal / Tax",   key: "gstin",                 label: "GSTIN" },
  { section: "Legal / Tax",   key: "pan",                    label: "PAN" },
  { section: "Legal / Tax",   key: "cin",                    label: "CIN" },
  { section: "Legal / Tax",   key: "msme_no",           label: "MSME Registration #" },
  { section: "Legal / Tax",   key: "place_of_supply",  label: "Place of Supply" },

  { section: "Banking",       key: "bank_name",        label: "Bank Name" },
  { section: "Banking",       key: "bank_account",     label: "Account Number" },
  { section: "Banking",       key: "ifsc",                    label: "IFSC" },
  { section: "Banking",       key: "swift",                   label: "SWIFT" },

  { section: "Signatory",     key: "signatory_name",  label: "Signatory Name" },
  { section: "Signatory",     key: "signatory_title",   label: "Signatory Title (e.g. Authorised Signatory)" },
];

const SECTION_ICON = {
  "Brand":         Building2,
  "Address":       Settings,
  "Legal / Tax":   ShieldCheck,
  "Banking":       Banknote,
  "Signatory":     ShieldCheck,
};


export default function CompanyProfile() {
  const nav = useNavigate();
  const [form, setForm]   = useState({});
  const [busy, setBusy]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null);
  const fileRef = useRef(null);
  const governed = useGovernedFields("company_profile");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/commercial/company-profile");
      setProfile(data || {});
      const cleaned = { ...data };
      delete cleaned.signature_b64;
      delete cleaned.key;
      setForm(cleaned);
    } catch (e) { toast.error("Failed to load profile"); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      FIELDS.forEach(({ key }) => { if (!governed[key]) patch[key] = form[key] ?? null; });
      await api.put("/commercial/company-profile", patch);
      toast.success("Company profile saved · governed fields come from KYC Readiness · all future PDFs will use these values");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)/.test(file.type)) {
      toast.error("Upload a PNG or JPG signature image"); return;
    }
    if (file.size > 675_000) {
      toast.error("Signature file too large (max ~675 KB)"); return;
    }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve((r.result || "").toString());
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.put("/commercial/company-profile/signature",
        { signature_b64: b64, filename: file.name });
      toast.success("Signature uploaded · will appear above 'Authorised Signatory'");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const clearSig = async () => {
    if (!window.confirm("Remove the uploaded signature?  All future PDFs will fall back to a plain 'Digital document' line.")) return;
    try {
      await api.delete("/commercial/company-profile/signature");
      toast.success("Signature cleared");
      load();
    } catch (e) { toast.error("Clear failed"); }
  };

  const sections = FIELDS.reduce((acc, f) => {
    (acc[f.section] = acc[f.section] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl space-y-5" data-testid="company-profile-page">
      <div>
        <button onClick={() => nav(-1)}
          className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-1"
          data-testid="back">
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <div className="text-[11px] uppercase tracking-[.16em] text-slate-500">
          Suntronix ONE · Commercial Settings
        </div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
          <Settings className="h-6 w-6 text-emerald-700" />
          Company Profile
          <Badge className="text-[10px] ml-2 bg-emerald-50 border-emerald-200 text-emerald-800">
            Quotation PDF source of truth
          </Badge>
        </h1>
        <p className="text-xs text-slate-500 mt-1 max-w-3xl">
          Every field below appears on the Quotation PDF — company name,
          address, GSTIN, MSME registration, banking, signatory and sales
          contact.  Whatever you save here is what customers see.
        </p>
      </div>
      <GovernedBanner count={FIELDS.filter((f) => governed[f.key]).length} testid="cp-governed-banner" />

      {Object.entries(sections).map(([section, fields]) => {
        const Icon = SECTION_ICON[section] || Settings;
        return (
          <div key={section} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-[12px] font-heading font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <Icon className="h-4 w-4 text-emerald-700" /> {section}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fields.map(f => (
                <div key={f.key}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between gap-2">{f.label}{governed[f.key] && <GovernedTag masterKey={governed[f.key]} testid={`cp-governed-${f.key}`} />}</div>
                  <Input value={form[f.key] ?? ""}
                    readOnly={!!governed[f.key]}
                    className={governed[f.key] ? "bg-emerald-50/60 text-slate-700 cursor-not-allowed border-emerald-200" : ""}
                    onChange={(e) => !governed[f.key] && setForm(s => ({ ...s, [f.key]: e.target.value }))}
                    data-testid={`cp-${f.key}`} />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Signature */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-[12px] font-heading font-semibold text-slate-800 flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-emerald-700" /> Authorised Signature
        </div>
        <div className="flex items-start gap-4">
          <div className="w-48 h-24 border border-dashed border-slate-300 rounded flex items-center justify-center bg-slate-50"
               data-testid="signature-preview">
            {profile?.signature_b64
              ? <img src={`data:image/png;base64,${profile.signature_b64}`}
                       alt="Signature" className="max-w-full max-h-full" />
              : <span className="text-[11px] text-slate-400">No signature</span>}
          </div>
          <div className="flex-1 space-y-2">
            <div className="text-[11px] text-slate-600">
              PNG or JPG with a transparent background works best (max 675 KB).
              Stamped just above the &quot;Authorised Signatory&quot; line on every
              issued and previewed PDF.
              {profile?.signature_uploaded_at && (
                <div className="mt-1 text-slate-500">
                  Uploaded {new Date(profile.signature_uploaded_at).toLocaleString()} by {profile.signature_uploaded_by}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg"
                onChange={(e) => upload(e.target.files?.[0])}
                className="hidden" data-testid="signature-file-input" />
              <Button variant="create" size="sm"
                onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="signature-upload">
                <Upload className="h-3 w-3 mr-1.5" />
                {uploading ? "Uploading…" : "Upload signature"}
              </Button>
              {profile?.signature_b64 && (
                <Button size="sm" variant="outline" onClick={clearSig}
                  className="text-rose-700 hover:bg-rose-50"
                  data-testid="signature-clear">
                  <Trash2 className="h-3 w-3 mr-1.5" /> Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 sticky bottom-4 bg-slate-100/80 backdrop-blur-sm p-3 rounded-lg border border-slate-200">
        <Button variant="outline" onClick={load} disabled={busy} data-testid="cp-reload">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
          Reload
        </Button>
        <Button onClick={save} disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800 text-white ml-auto"
          data-testid="cp-save">
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : "Save Company Profile"}
        </Button>
      </div>
    </div>
  );
}
